// Deroi · Cost — Plan vs Actual, per fabric line.
//
// THIS SCREEN SHOWS NO TOTAL AND NO MARGIN, DELIBERATELY. The plan on these rows is ~Rs 392/kg
// and the only measured actual is yarn at ~Rs 195/kg, so roughly Rs 197 per kilo of planned cost
// has nothing behind it. A margin drawn from that would be overstated by that amount — and would
// be believed, because it would look precise. What is shown instead is a per-head breakdown, a
// COVERAGE ratio, and the one variance that is real.
//
// THREE STATES, NOT TWO. measured / recorded-but-unlinked / not captured. The middle one is
// knitting: the money exists on job-work orders and has never been posted to the ledger. It gets
// its own colour so it cannot be read as "never captured" — its fix is plumbing, not design.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const RED = '#A32D2D';
const inr = (v) => v === null || v === undefined ? '—'
  : '₹' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kg = (v) => v === null || v === undefined ? '—'
  : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' kg';

// The legend is part of the screen, not a footnote: the difference between "we have not measured
// this" and "we measured it and never posted it" is the whole reason this table exists.
const STATE = {
  measured: { label: 'Measured', bg: '#EAF7EE', fg: '#166534',
    note: 'A recorded actual, from the Deroi cost ledger.' },
  unlinked: { label: 'Recorded, not linked', bg: '#FEF3E2', fg: '#B45309',
    note: 'The money exists on a job-work order but has never been posted to the cost ledger.' },
  absent: { label: 'Not captured', bg: '#F3F4F6', fg: '#6B7280',
    note: 'No document records this cost anywhere today.' },
};

const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600,
  borderBottom: '2px solid #eee', fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', fontSize: 13 };

function StateTag({ state }) {
  const m = STATE[state] || STATE.absent;
  return <span title={m.note} style={{ background: m.bg, color: m.fg, padding: '2px 8px',
    borderRadius: 10, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'help' }}>{m.label}</span>;
}

function HeadTable({ heads }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, background: '#FCFCFD' }}>
      <thead><tr>
        {['Head', 'Planned', 'Actual', 'State', 'Source'].map(h => (
          <th key={h} style={{ ...th, fontSize: 11, padding: '7px 10px' }}>{h}</th>))}
      </tr></thead>
      <tbody>
        {heads.map(h => (
          <tr key={h.key} style={{ borderTop: '1px solid #f0f0f0' }}>
            <td style={{ ...td, padding: '7px 10px', fontWeight: h.state === 'measured' ? 700 : 400 }}>{h.label}</td>
            <td style={{ ...td, padding: '7px 10px' }}>
              {h.planned === null ? '—' : (h.is_pct ? h.planned + '%' : inr(h.planned))}</td>
            <td style={{ ...td, padding: '7px 10px', fontWeight: 700,
              color: h.state === 'measured' ? '#166534' : h.state === 'unlinked' ? '#B45309' : '#bbb' }}>
              {h.actual === null ? '—' : inr(h.actual)}</td>
            <td style={{ ...td, padding: '7px 10px' }}><StateTag state={h.state} /></td>
            <td style={{ ...td, padding: '7px 10px', fontSize: 11, color: '#888' }}>{h.source || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DeroiCostCompare() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);       // expanded line_id

  const load = useCallback(() => {
    api.get('/deroi/cost-comparison')
      .then(r => setData(r.data.data))
      .catch(e => setErr(e.response?.data?.error || 'Could not load the comparison'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ background: '#FCEBEB', color: RED, padding: '10px 14px', borderRadius: 6, fontSize: 13 }}>{err}</div>;
  if (!data) return <div style={{ color: '#aaa', fontSize: 13, padding: 20 }}>Loading…</div>;

  const h = data.headline;
  const over = h && h.variance_per_kg > 0;

  return (
    <div>
      {/* THE FINDING, not a summary. It is the only comparison that exists and it is unfavourable,
          so it leads rather than sitting at the foot of a table nobody scrolls to. */}
      {h && (
        <div style={{ background: over ? '#FFF7E6' : '#EAF7EE', border: '1px solid ' + (over ? '#F0D089' : '#BFE3CB'),
          borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: over ? '#8A6D00' : '#166534', marginBottom: 6 }}>
            Yarn is running {Math.abs(h.variance_pct)}% {over ? 'OVER' : 'under'} plan
          </div>
          <div style={{ fontSize: 13, color: '#4A4A4A' }}>
            Planned <strong>{inr(h.planned_per_kg)}/kg</strong> · actual <strong>{inr(h.actual_per_kg)}/kg</strong> ·{' '}
            <strong style={{ color: over ? RED : '#166534' }}>
              {over ? '+' : ''}{inr(h.variance_per_kg)}/kg</strong>{' '}
            across {kg(h.kg)} on {h.rows} fabric line{h.rows === 1 ? '' : 's'}.
          </div>
          <div style={{ fontSize: 11.5, color: '#8A6D00', marginTop: 6 }}>
            Weighted by the kilos behind each line, so a large order is not one vote.
          </div>
        </div>
      )}

      {/* The caveat rides in the payload rather than living here, so the figures cannot be
          rendered anywhere without it. */}
      <div style={{ background: '#F7F7FB', border: '1px solid #E3E2F0', borderRadius: 8,
        padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#4A4A6A', lineHeight: 1.5 }}>
        <strong style={{ color: '#3B3A8F' }}>No total, and no margin.</strong> {data.caveat}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {Object.entries(STATE).map(([k, m]) => (
          <span key={k} style={{ fontSize: 11, color: '#666' }}>
            <StateTag state={k} /> <span style={{ marginLeft: 4 }}>{m.note}</span>
          </span>
        ))}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['GPO', 'Fabric', 'Shade', 'Estimate', 'Planned ₹/kg', 'Yarn actual ₹/kg', 'Yarn variance', 'Planned cost with an actual', ''].map(x => (
            <th key={x} style={th}>{x}</th>))}
        </tr></thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>
              No fabric line can be compared yet.</td></tr>)}
          {data.rows.map(r => {
            const yarn = r.heads.find(x => x.key === 'yarn') || {};
            const v = (yarn.planned !== null && yarn.actual !== null) ? +(yarn.actual - yarn.planned).toFixed(2) : null;
            const isOpen = open === r.line_id;
            return (
              <React.Fragment key={r.line_id || r.gpo_no}>
                <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{r.gpo_no}</td>
                  <td style={td}>{r.item_desc || '—'}</td>
                  <td style={td}>{r.shade || '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.estimate_no || '—'}
                    {r.plan_status && r.plan_status !== 'approved' &&
                      <span style={{ marginLeft: 5, fontSize: 10, color: '#B45309' }}>({r.plan_status})</span>}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{inr(r.plan_total_per_kg)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#166534' }}>{inr(yarn.actual)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: v > 0 ? RED : '#166534' }}>
                    {v === null ? '—' : (v > 0 ? '+' : '') + inr(v)}</td>
                  {/* NOT "coverage of cost" as a share of anything spent — the share of the PLAN
                      that has a measured actual. Said in words on the cell so it cannot be read
                      as a completion percentage. */}
                  <td style={{ ...td }}>
                    {/* "of plan" read as "of the plan TOTAL", which it is not: the denominator is
                        the sum of populated money heads, excluding margin and the loss rates.
                        Against the plan total the same row is 8.6 points lower, so the short
                        label was worth more than it cost. It wraps rather than forcing the
                        column wider. */}
                    <span title={`${r.measured_heads} of ${r.money_heads} planned cost heads have a measured actual. Margin and the process-loss rates are excluded — they are not costs.`}
                      style={{ background: '#EEEDF8', color: '#3B3A8F', padding: '3px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 700, cursor: 'help', display: 'inline-block', lineHeight: 1.45 }}>
                      {r.coverage_pct === null ? '—' : r.coverage_pct + '% of planned cost heads'}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button type="button" onClick={() => setOpen(isOpen ? null : r.line_id)}
                      style={{ padding: '4px 10px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 5,
                        color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {isOpen ? '▲ Hide heads' : '▼ All heads'}</button>
                  </td>
                </tr>
                {isOpen && (
                  <tr><td colSpan={9} style={{ padding: '0 10px 14px' }}><HeadTable heads={r.heads} /></td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* EXCLUSIONS ARE SHOWN, NOT FILTERED. An exclusion a reader can see is a specification for
          the next fix; one that vanishes is a number nobody can reconcile. */}
      {data.excluded && data.excluded.length > 0 && (
        <div style={{ marginTop: 22, border: '1px solid #E3E2F0', borderRadius: 8, padding: '12px 16px', background: '#FCFCFD' }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: '#3B3A8F', marginBottom: 6 }}>
            Excluded — {data.excluded.length} order{data.excluded.length === 1 ? '' : 's'}, {data.excluded_lines} fabric lines
          </div>
          <div style={{ fontSize: 12.5, color: '#666', lineHeight: 1.5, marginBottom: 10 }}>
            The yarn cost on these orders is recorded against the <strong>purchase order</strong>, which carries
            more than one fabric. It cannot be attributed to one of them until the yarn issue names the fabric
            line. They are listed rather than hidden, because the figures below are real money that this
            comparison cannot yet place.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr>{['GPO', 'Fabric lines', 'Yarn recorded', 'Value'].map(x =>
              <th key={x} style={{ ...th, fontSize: 11, padding: '6px 10px' }}>{x}</th>)}</tr></thead>
            <tbody>
              {data.excluded.map(x => (
                <tr key={x.gpo_no} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ ...td, padding: '6px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{x.gpo_no}</td>
                  <td style={{ ...td, padding: '6px 10px' }}>{x.n_lines}</td>
                  <td style={{ ...td, padding: '6px 10px' }}>{kg(x.kg)}</td>
                  <td style={{ ...td, padding: '6px 10px' }}>{inr(x.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
